"use client";
import React, { useRef, useEffect } from "react";
import Image from "next/image";
import gsap from "gsap";
import { SplitText } from "gsap/SplitText";
import { getStrapiMedia } from "@/utils/api-helpers";
import BottomCurves from "../curves/bottomCurves";

gsap.registerPlugin(SplitText);

export default function PreLoader({ data }: { data: any }) {
    const containerRef: any = useRef(null);
    const carouselRef: any = useRef(null);
    const heroDescRef: any = useRef(null);

    useEffect(() => {
        const splitWord = document.querySelectorAll(".split-word");

        splitWord.forEach((word) => {
            const split = new SplitText(word, {
                type: "words,chars",
                aria: "none",
            });
            const chars = split.chars;

            gsap.to(word, {
                opacity: 1,
            });

            const tl = gsap.timeline(chars);
            tl.from(chars, {
                duration: 0.8,
                opacity: 0,
                scale: 0,
                transformOrigin: "center",
                ease: "back",
                stagger: {
                    amount: 1,
                    grid: [1, 15],
                    ease: "none",
                    from: "random",
                },
            });

            tl.to(word, {
                scale: 1,
            });

            tl.to(
                heroDescRef.current,
                {
                    opacity: 1,
                    y: 0,
                },
                "-=0.65",
            );

            Array.from(carouselRef.current.children).forEach(
                (child: any, index: number) => {
                    // tl.to(
                    //     child,
                    //     {
                    //         opacity: 1,
                    //     },
                    //     index === 0 ? undefined : "<",
                    // );

                    child.classList.add("animate-slideToRight");
                },
            );
        });
    }, []);

    return (
        <section
            className="relative min-h-[100lvh] w-full flex flex-col gap-y-4 md:justify-between pt-[80px] pb-[60px] sm:py-[11.979vw] items-start bg-gradientPurple px-[8.33vw]"
            ref={containerRef}
        >
            <div className="relative flex flex-col text-[11vw] sm:text-[9.375vw] leading-[1] font-black uppercase z-10 preloadtext">
                <span className="relative text-white split-word opacity-0 scale-110 w-fit">
                    MADE
                </span>
                <span className="relative text-[#CBA1F5] split-word opacity-0 scale-110 w-fit">
                    DIFFERENTLY
                </span>
            </div>

            <div
                className="relative md:absolute max-w-[90%] sm:max-w-[25%] sm:min-w-[330px] w-full top-[25%] md:top-[45%] md:right-[10%] opacity-0 translate-y-7 z-10"
                ref={heroDescRef}
            >
                <p className="text-white">{data.description}</p>
            </div>

            {/* Bg Carousel */}
            <div
                className="relative md:h-fit h-full grow md:absolute w-max  md:-top-[25%] left-0 md:bottom-0 md:m-auto z-0 md:items-center items-start inline-flex pb-[180px] md:pb-0"
                ref={carouselRef}
            >
                {[...Array(2)].map((_, i) => (
                    <div
                        className="inline-flex opacity-1 items-center pr-[15vw] gap-x-[15vw]"
                        key={i}
                    >
                        {data.imageCarousel.map(
                            (content: any, index: number) => {
                                const mediaDimension = content.dimension
                                    .replace("aspect ratio", "")
                                    .replace(":", "/")
                                    .trim();
                                const imageStyle = {
                                    objectFit: "cover",
                                    borderRadius: "1rem",
                                };

                                const classes: any = {
                                    "1/1": "aspect-square min-w-[200px] sm:min-w-[160px] min-h-[160px] w-[15vw]",
                                    "16/9": "aspect-[19/6] min-h-[170px] sm:min-h-[200px] w-[18.75vw]",
                                    "9/16": "aspect-[290/430] min-w-[160px] sm:min-w-[200px] h-[18.75vw]",
                                    "4/3": "aspect-[4/3] min-w-[200px] sm:min-w-[200px] w-[18.75vw]",
                                };
                                const imageDimensions: Record<
                                    string,
                                    { width: number; height: number; sizes: string }
                                > = {
                                    "1/1": {
                                        width: 320,
                                        height: 320,
                                        sizes: "(min-width: 640px) 15vw, 200px",
                                    },
                                    "16/9": {
                                        width: 480,
                                        height: 152,
                                        sizes: "(min-width: 640px) 18.75vw, 300px",
                                    },
                                    "9/16": {
                                        width: 290,
                                        height: 430,
                                        sizes: "(min-width: 640px) 200px, 160px",
                                    },
                                    "4/3": {
                                        width: 400,
                                        height: 300,
                                        sizes: "(min-width: 640px) 18.75vw, 200px",
                                    },
                                };

                                const position =
                                    index % 2 !== 0
                                        ? "md:-translate-y-[55%] "
                                        : "md:translate-y-[25%] -translate-y-[0%]";
                                const dimensions =
                                    imageDimensions[mediaDimension] ||
                                    imageDimensions["1/1"];
                                const isVideo =
                                    content.image.url
                                        ?.split("?")[0]
                                        .toLowerCase()
                                        .endsWith(".mp4") ?? false;
                                const isPriorityImage = i === 0 && index < 2;

                                return (
                                    <div
                                        key={`carousel-${index}-${i}`}
                                        className={`${
                                            classes[mediaDimension] || ""
                                        } ${position}`}
                                    >
                                        {isVideo ? (
                                            <video
                                                src={getStrapiMedia(
                                                    content.image.url,
                                                )}
                                                width={dimensions.width}
                                                height={dimensions.height}
                                                className="rounded-[1rem] object-cover"
                                                autoPlay
                                                loop
                                                muted
                                                playsInline
                                                preload="metadata"
                                            ></video>
                                        ) : (
                                            <Image
                                                src={getStrapiMedia(
                                                    content.image.url,
                                                )}
                                                alt={content.image.name}
                                                width={dimensions.width}
                                                height={dimensions.height}
                                                sizes={dimensions.sizes}
                                                style={imageStyle as any}
                                                priority={isPriorityImage}
                                            />
                                        )}
                                    </div>
                                );
                            },
                        )}
                    </div>
                ))}
            </div>
            <BottomCurves
                color={data.waveBgColor.waveBgColor}
                direction="left"
            />
        </section>
    );
}
